import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardFooter,
} from "../../components/misc/Card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/misc/Table";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../../components/misc/Pagination";
import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "../../utils/classMerger";
import type { UserQuotaData } from "./types";
import Spinner from "../../components/misc/Spinner";
import SimpleTooltip from "../../components/misc/SimpleTooltip";

type SortColumn =
    | "userName"
    | "bandwidthUsed"
    | "storageUsed"
    | "listingsUsed"
    | "rentcastUsage"
    | "postsUsed"
    | "imageTagsUsed"
    | "videosUsed";

interface UserQuotaListProps {
    users: UserQuotaData[];
    page: number;
    setPage: (n: number) => void;
    limit: number;
    total: number;
    searchTerm: string;
    setSearchTerm: (s: string) => void;
    isMobile: boolean;
    sortBy?: string;
    sortDirection?: "asc" | "desc";
    onSortChange?: (column: string, direction: "asc" | "desc") => void;
    loading?: boolean;
}

const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export function UserQuotaList({
    users,
    page,
    setPage,
    limit,
    total,
    searchTerm,
    setSearchTerm,
    isMobile,
    sortBy,
    sortDirection,
    onSortChange,
    loading
}: UserQuotaListProps) {
  const totalPages = Math.ceil(total / limit);

  // Handle column header click for sorting
  const handleSort = (column: SortColumn) => {
    if (!onSortChange) return;

    if (sortBy === column) {
      // Same column, toggle direction
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      onSortChange(column, newDirection);
    } else {
      // New column, default to ascending
      onSortChange(column, "asc");
    }
  };

  // Render sort icon
  const renderSortIcon = (column: SortColumn) => {
    if (!onSortChange || sortBy !== column) {
      return null;
    }

    return sortDirection === "asc" ? (
      <ChevronUp className="ml-1 h-4 w-4" />
    ) : (
      <ChevronDown className="ml-1 h-4 w-4" />
    );
  };

  // Simplified helper function for usage status
  const getUsageStatus = (
    used: number,
    softLimit: number,
    hardLimit: number
  ) => {
    if (hardLimit === 0) return "normal"; // No limit set

    if (used >= hardLimit) return "critical"; // Red - Hard limit reached
    if (used >= softLimit) return "warning"; // Yellow - Soft limit reached
    return "normal"; // No color - Normal usage
  };

  // Simplified status display
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "critical":
        return {
          bgColor: "bg-red-50",
          textColor: "text-red-700",
          borderColor: "border-red-200",
          icon: "🚫",
        };
      case "warning":
        return {
          bgColor: "bg-yellow-50",
          textColor: "text-yellow-700",
          borderColor: "border-yellow-200",
          icon: "⚠️",
        };
      default:
        return {
          bgColor: "",
          textColor: "text-gray-700",
          borderColor: "",
          icon: "",
        };
    }
  };

  const getTooltipMessage = (
    status: string,
    used: number,
    softLimit: number,
    hardLimit: number,
    type: string
  ) => {
    const typeName = type.charAt(0).toUpperCase() + type.slice(1);

    switch (status) {
      case "critical":
        return `🚫 ${typeName} hard limit reached!`;
      case "warning":
        return `⚠️ ${typeName} warning! You've reached your soft limit of ${formatBytes(
          softLimit * 1024 * 1024
        )}. Your hard limit is ${formatBytes(hardLimit * 1024 * 1024)}.`;
      default:
        return null; // No tooltip for normal usage
    }
  };

  const getTooltipBgColor = (status: string) => {
    switch (status) {
      case "critical":
        return "bg-red-100"; // Red background for hard limit
      case "warning":
        return "bg-yellow-100"; // Yellow background for soft limit
      default:
        return "";
    }
  };

  return (
    <Card className={cn()}>
      <CardHeader>
        <CardTitle>User Quota Usage</CardTitle>
        {/* filters */}
        <div
          className={`flex ${isMobile ? "flex-col" : "flex-row"} pt-4 gap-x-10`}
        >
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="border rounded px-2 py-1 flex-1"
          />
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className={
                  onSortChange ? "cursor-pointer hover:bg-gray-50" : ""
                }
                onClick={
                  onSortChange ? () => handleSort("userName") : undefined
                }
              >
                User Name
                {renderSortIcon("userName")}
              </TableHead>
              <TableHead
                className={
                  onSortChange
                    ? "text-center cursor-pointer hover:bg-gray-50"
                    : "text-center"
                }
                onClick={
                  onSortChange ? () => handleSort("bandwidthUsed") : undefined
                }
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Bandwidth</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    Used / Limit
                  </span>
                  {renderSortIcon("bandwidthUsed")}
                </div>
              </TableHead>
              <TableHead
                className={
                  onSortChange
                    ? "text-center cursor-pointer hover:bg-gray-50"
                    : "text-center"
                }
                onClick={
                  onSortChange ? () => handleSort("storageUsed") : undefined
                }
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Storage</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    Used / Limit
                  </span>
                  {renderSortIcon("storageUsed")}
                </div>
              </TableHead>
              <TableHead
                className={
                  onSortChange
                    ? "text-center cursor-pointer hover:bg-gray-50"
                    : "text-center"
                }
                onClick={
                  onSortChange ? () => handleSort("listingsUsed") : undefined
                }
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Listings</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    Used / Limit
                  </span>
                  {renderSortIcon("listingsUsed")}
                </div>
              </TableHead>
              <TableHead
                className={
                  onSortChange
                    ? "text-center cursor-pointer hover:bg-gray-50"
                    : "text-center"
                }
                onClick={
                  onSortChange ? () => handleSort("rentcastUsage") : undefined
                }
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Rentcast</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    Used / Limit
                  </span>
                  {renderSortIcon("rentcastUsage")}
                </div>
              </TableHead>
              <TableHead
                className={
                  onSortChange
                    ? "text-center cursor-pointer hover:bg-gray-50"
                    : "text-center"
                }
                onClick={
                  onSortChange ? () => handleSort("postsUsed") : undefined
                }
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Posts</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    Used / Limit
                  </span>
                  {renderSortIcon("postsUsed")}
                </div>
              </TableHead>
              <TableHead
                className={
                  onSortChange
                    ? "text-center cursor-pointer hover:bg-gray-50"
                    : "text-center"
                }
                onClick={
                  onSortChange ? () => handleSort("imageTagsUsed") : undefined
                }
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Image Tags</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    Used / Limit
                  </span>
                  {renderSortIcon("imageTagsUsed")}
                </div>
              </TableHead>
              <TableHead
                className={
                  onSortChange
                    ? "text-center cursor-pointer hover:bg-gray-50"
                    : "text-center"
                }
                onClick={
                  onSortChange ? () => handleSort("videosUsed") : undefined
                }
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Videos</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    Used / Limit
                  </span>
                  {renderSortIcon("videosUsed")}
                </div>
              </TableHead>
              <TableHead className="text-center">
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Zapcap Seconds</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    Used / Limit
                  </span>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Loading state with spinner
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <Spinner />
                    {/* <p className="text-gray-500">Loading users...</p> */}
                  </div>
                </TableCell>
              </TableRow>
            ) : users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell className="font-medium">
                    <div>
                      <div>{user.userName}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </TableCell>

                  <TableCell className="text-center">
                    {(() => {
                      const bandwidthStatus = getUsageStatus(
                        user.bandwidthUsed,
                        user.monthlyTransferWarn || 0,
                        user.monthlyTransferLimit || 0
                      );
                      const statusDisplay = getStatusDisplay(bandwidthStatus);
                      const tooltipMessage = getTooltipMessage(
                        bandwidthStatus,
                        user.bandwidthUsed,
                        user.monthlyTransferWarn || 0,
                        user.monthlyTransferLimit || 0,
                        "bandwidth"
                      );
                      const tooltipBgColor = getTooltipBgColor(bandwidthStatus);

                      const cellContent = (
                        <div
                          className={`p-2 rounded-lg ${statusDisplay.bgColor} ${
                            statusDisplay.borderColor
                              ? `border ${statusDisplay.borderColor}`
                              : ""
                          }`}
                        >
                          <div
                            className={`text-sm font-medium ${statusDisplay.textColor}`}
                          >
                            {statusDisplay.icon && (
                              <span className="mr-1">{statusDisplay.icon}</span>
                            )}
                            {formatBytes(user.bandwidthUsed * 1024 * 1024)} /{" "}
                            {formatBytes(
                              (user.monthlyTransferLimit || 0) * 1024 * 1024
                            )}
                          </div>
                        </div>
                      );

                      if (tooltipMessage) {
                        return (
                          <SimpleTooltip
                            message={tooltipMessage}
                            tooltipClassName={tooltipBgColor}
                            visible={true}
                          >
                            {cellContent}
                          </SimpleTooltip>
                        );
                      }

                      return cellContent;
                    })()}
                  </TableCell>

                  <TableCell className="text-center">
                    {(() => {
                      const storageStatus = getUsageStatus(
                        user.storageUsed,
                        user.storageWarn || 0,
                        user.storageLimit || 0
                      );
                      const statusDisplay = getStatusDisplay(storageStatus);
                      const tooltipMessage = getTooltipMessage(
                        storageStatus,
                        user.storageUsed,
                        user.storageWarn || 0,
                        user.storageLimit || 0,
                        "storage"
                      );
                      const tooltipBgColor = getTooltipBgColor(storageStatus);

                      const cellContent = (
                        <div
                          className={`p-2 rounded-lg ${statusDisplay.bgColor} ${
                            statusDisplay.borderColor
                              ? `border ${statusDisplay.borderColor}`
                              : ""
                          }`}
                        >
                          <div
                            className={`text-sm font-medium ${statusDisplay.textColor}`}
                          >
                            {statusDisplay.icon && (
                              <span className="mr-1">{statusDisplay.icon}</span>
                            )}
                            {formatBytes(user.storageUsed * 1024 * 1024)} /{" "}
                            {formatBytes(
                              (user.storageLimit || 0) * 1024 * 1024
                            )}
                          </div>
                        </div>
                      );

                      if (tooltipMessage) {
                        return (
                          <SimpleTooltip
                            message={tooltipMessage}
                            tooltipClassName={tooltipBgColor}
                            visible={true}
                          >
                            {cellContent}
                          </SimpleTooltip>
                        );
                      }

                      return cellContent;
                    })()}
                  </TableCell>

                  <TableCell className="text-center">
                    {(() => {
                      const listingsStatus = getUsageStatus(
                        user.listingsUsed || 0,
                        user.listingWarn || 0,
                        user.listingLimit || 0
                      );
                      const statusDisplay = getStatusDisplay(listingsStatus);
                      const tooltipMessage = getTooltipMessage(
                        listingsStatus,
                        user.listingsUsed || 0,
                        user.listingWarn || 0,
                        user.listingLimit || 0,
                        "listings"
                      );
                      const tooltipBgColor = getTooltipBgColor(listingsStatus);

                      const cellContent = (
                        <div
                          className={`p-2 rounded-lg ${statusDisplay.bgColor} ${
                            statusDisplay.borderColor
                              ? `border ${statusDisplay.borderColor}`
                              : ""
                          }`}
                        >
                          <div
                            className={`text-sm font-medium ${statusDisplay.textColor}`}
                          >
                            {statusDisplay.icon && (
                              <span className="mr-1">{statusDisplay.icon}</span>
                            )}
                            {user.listingsUsed || 0} / {user.listingLimit || 0}
                          </div>
                        </div>
                      );

                      if (tooltipMessage) {
                        return (
                          <SimpleTooltip
                            message={tooltipMessage}
                            tooltipClassName={tooltipBgColor}
                            visible={true}
                          >
                            {cellContent}
                          </SimpleTooltip>
                        );
                      }

                      return cellContent;
                    })()}
                  </TableCell>

                  <TableCell className="text-center">
                    {(() => {
                      const rentcastStatus = getUsageStatus(
                        user.rentcastUsage || 0,
                        user.rentcastWarn || 0,
                        user.rentcastLimit || 0
                      );
                      const statusDisplay = getStatusDisplay(rentcastStatus);
                      const tooltipMessage = getTooltipMessage(
                        rentcastStatus,
                        user.rentcastUsage || 0,
                        user.rentcastWarn || 0,
                        user.rentcastLimit || 0,
                        "rentcast"
                      );
                      const tooltipBgColor = getTooltipBgColor(rentcastStatus);

                      const cellContent = (
                        <div
                          className={`p-2 rounded-lg ${statusDisplay.bgColor} ${
                            statusDisplay.borderColor
                              ? `border ${statusDisplay.borderColor}`
                              : ""
                          }`}
                        >
                          <div
                            className={`text-sm font-medium ${statusDisplay.textColor}`}
                          >
                            {statusDisplay.icon && (
                              <span className="mr-1">{statusDisplay.icon}</span>
                            )}
                            {user?.rentcastUsage || 0} / {user?.rentcastLimit || 0}
                          </div>
                        </div>
                      );

                      if (tooltipMessage) {
                        return (
                          <SimpleTooltip
                            message={tooltipMessage}
                            tooltipClassName={tooltipBgColor}
                            visible={true}
                          >
                            {cellContent}
                          </SimpleTooltip>
                        );
                      }

                      return cellContent;
                    })()}
                  </TableCell>

                  <TableCell className="text-center">
                    {(() => {
                      const postsStatus = getUsageStatus(
                        user.postsUsed || 0,
                        user.postWarn || 0,
                        user.postLimit || 0
                      );
                      const statusDisplay = getStatusDisplay(postsStatus);
                      const tooltipMessage = getTooltipMessage(
                        postsStatus,
                        user.postsUsed || 0,
                        user.postWarn || 0,
                        user.postLimit || 0,
                        "posts"
                      );
                      const tooltipBgColor = getTooltipBgColor(postsStatus);

                      const cellContent = (
                        <div
                          className={`p-2 rounded-lg ${statusDisplay.bgColor} ${
                            statusDisplay.borderColor
                              ? `border ${statusDisplay.borderColor}`
                              : ""
                          }`}
                        >
                          <div
                            className={`text-sm font-medium ${statusDisplay.textColor}`}
                          >
                            {statusDisplay.icon && (
                              <span className="mr-1">{statusDisplay.icon}</span>
                            )}
                            {user.postsUsed || 0} / {user.postLimit || 0}
                          </div>
                        </div>
                      );

                      if (tooltipMessage) {
                        return (
                          <SimpleTooltip
                            message={tooltipMessage}
                            tooltipClassName={tooltipBgColor}
                            visible={true}
                          >
                            {cellContent}
                          </SimpleTooltip>
                        );
                      }

                      return cellContent;
                    })()}
                  </TableCell>

                  <TableCell className="text-center">
                    {(() => {
                      const imageTagsStatus = getUsageStatus(
                        user.imageTagsUsed || 0,
                        user.imageTagsWarn || 0,
                        user.imageTagsLimit || 0
                      );
                      const statusDisplay = getStatusDisplay(imageTagsStatus);
                      const tooltipMessage = getTooltipMessage(
                        imageTagsStatus,
                        user.imageTagsUsed || 0,
                        user.imageTagsWarn || 0,
                        user.imageTagsLimit || 0,
                        "imageTags"
                      );
                      const tooltipBgColor = getTooltipBgColor(imageTagsStatus);

                      const cellContent = (
                        <div
                          className={`p-2 rounded-lg ${statusDisplay.bgColor} ${
                            statusDisplay.borderColor
                              ? `border ${statusDisplay.borderColor}`
                              : ""
                          }`}
                        >
                          <div
                            className={`text-sm font-medium ${statusDisplay.textColor}`}
                          >
                            {statusDisplay.icon && (
                              <span className="mr-1">{statusDisplay.icon}</span>
                            )}
                            {user.imageTagsUsed || 0} /{" "}
                            {user.imageTagsLimit || 0}
                          </div>
                        </div>
                      );

                      if (tooltipMessage) {
                        return (
                          <SimpleTooltip
                            message={tooltipMessage}
                            tooltipClassName={tooltipBgColor}
                            visible={true}
                          >
                            {cellContent}
                          </SimpleTooltip>
                        );
                      }

                      return cellContent;
                    })()}
                  </TableCell>

                  <TableCell className="text-center">
                    {(() => {
                      const videosStatus = getUsageStatus(
                        user.videosUsed || 0,
                        user.imageVideosWarn || 0,
                        user.imageVideosLimit || 0
                      );
                      const statusDisplay = getStatusDisplay(videosStatus);
                      const tooltipMessage = getTooltipMessage(
                        videosStatus,
                        user.videosUsed || 0,
                        user.imageVideosWarn || 0,
                        user.imageVideosLimit || 0,
                        "videos"
                      );
                      const tooltipBgColor = getTooltipBgColor(videosStatus);

                      const cellContent = (
                        <div
                          className={`p-2 rounded-lg ${statusDisplay.bgColor} ${
                            statusDisplay.borderColor
                              ? `border ${statusDisplay.borderColor}`
                              : ""
                          }`}
                        >
                          <div
                            className={`text-sm font-medium ${statusDisplay.textColor}`}
                          >
                            {statusDisplay.icon && (
                              <span className="mr-1">{statusDisplay.icon}</span>
                            )}
                            {user.videosUsed || 0} /{" "}
                            {user.imageVideosLimit || 0}
                          </div>
                        </div>
                      );

                      if (tooltipMessage) {
                        return (
                          <SimpleTooltip
                            message={tooltipMessage}
                            tooltipClassName={tooltipBgColor}
                            visible={true}
                          >
                            {cellContent}
                          </SimpleTooltip>
                        );
                      }

                      return cellContent;
                    })()}
                  </TableCell>

                  {/* Caption Video — uses zapcapSecondsUsage / zapcapSecondsLimit (seconds, no soft limit) */}
                  <TableCell className="text-center">
                    {(() => {
                      const zapcapUsed = user.zapcapSecondsUsage || 0;
                      const zapcapLimit = user.zapcapSecondsLimit || 0;
                      // No soft-limit field for zapcap; only flag critical when hard limit is reached.
                      const zapcapStatus = getUsageStatus(zapcapUsed, zapcapLimit, zapcapLimit);
                      const statusDisplay = getStatusDisplay(zapcapStatus);

                      return (
                        <div
                          className={`p-2 rounded-lg ${statusDisplay.bgColor} ${
                            statusDisplay.borderColor
                              ? `border ${statusDisplay.borderColor}`
                              : ""
                          }`}
                        >
                          <div className={`text-sm font-medium ${statusDisplay.textColor}`}>
                            {statusDisplay.icon && (
                              <span className="mr-1">{statusDisplay.icon}</span>
                            )}
                            {zapcapUsed} / {zapcapLimit}
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-6 text-muted-foreground"
                >
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
      {totalPages > 1 && (
        <CardFooter>
          <Pagination className="w-full">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage(Math.max(page - 1, 1))}
                  className={
                    page <= 1
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>

              {/* Show first page */}
              {page > 2 && (
                <>
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => setPage(1)}
                      className="cursor-pointer"
                    >
                      1
                    </PaginationLink>
                  </PaginationItem>
                  {page > 3 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                </>
              )}

              {/* Show current page and neighbors */}
              {page > 1 && (
                <PaginationItem>
                  <PaginationLink
                    onClick={() => setPage(page - 1)}
                    className="cursor-pointer"
                  >
                    {page - 1}
                  </PaginationLink>
                </PaginationItem>
              )}

              <PaginationItem>
                <PaginationLink isActive className="cursor-pointer">
                  {page}
                </PaginationLink>
              </PaginationItem>

              {page < totalPages && (
                <PaginationItem>
                  <PaginationLink
                    onClick={() => setPage(page + 1)}
                    className="cursor-pointer"
                  >
                    {page + 1}
                  </PaginationLink>
                </PaginationItem>
              )}

              {/* Show last page */}
              {page < totalPages - 1 && (
                <>
                  {page < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => setPage(totalPages)}
                      className="cursor-pointer"
                    >
                      {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                </>
              )}

              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage(Math.min(page + 1, totalPages))}
                  className={
                    page >= totalPages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </CardFooter>
      )}
    </Card>
  );
}
